import React, {useEffect, useCallback, useState, useRef} from 'react'
import {
    Flex,
    Box,
    useMultiStyleConfig,
    useBreakpointValue,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useParams, useRouteMatch} from 'react-router'
import {useIntl} from 'react-intl'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'

// PARTIALS
import Breadcrumb from '../breadcrumb'
import {RecipeHeroBanner} from '../editorial-components'
import IngredientsMenu from './partials/ingredients-menu'
import RecipeStepsMenu from './partials/recipe-steps-menu'
import RecipeProductSuggestions from './partials/recipe-product-suggestions'
import {useGetRecipeDetails} from './utils/use-get-recipe-info'
import LoadingSpinner from '../loading-spinner'

const CONTENT_TYPE_RECIPE = 'cooking_recipe'

const RecipeDetails = () => {
    const [productsUsedInRecipe, setProductsUsedInRecipe] = useState([])
    const [toolsUsedInRecipe, setToolsUsedInRecipe] = useState([])
    const [darkBgHeight, setDarkBgHeight] = useState(0)

    const ingredientsRef = useRef(null)
    const stepsRef = useRef(null)

    const {recipe_id} = useParams()
    const path = useRouteMatch()
    const {getTokenWhenReady} = useAccessToken()
    const api = useCommerceApi()
    const styles = useMultiStyleConfig('RecipeDetails', {})
    const {details} = styles || {}
    const intl = useIntl()

    const {data: recipe} = useGetRecipeDetails(recipe_id, CONTENT_TYPE_RECIPE)
    const utensilsList = recipe?.[0]?.products_utensils || {}

    const recipeProductsSKUs = utensilsList?.product_model?.map((p) => p?.title) || []
    const recipeToolsSKUs = utensilsList?.tools?.map((p) => p?.title) || []

    // Fetch product details
    const handleItemSearch = useCallback(
        async (codes) => {
            try {
                const token = await getTokenWhenReady()
                return await api.shopperProducts.getProducts({
                    parameters: {ids: codes, allImages: true},
                    headers: {Authorization: `Bearer ${token}`},
                })
            } catch (err) {
                console.error('Error fetching product details:', err)
                return null
            }
        },
        [api, getTokenWhenReady],
    )

    useEffect(() => {
        if (!recipeProductsSKUs.length) return

        const fetchProducts = async (codes, type) => {
            const result = await handleItemSearch(codes)
            if (result?.data?.length > 0) {
                type === 'product_model'
                    ? setProductsUsedInRecipe(result.data)
                    : setToolsUsedInRecipe(result.data)
            }
        }

        fetchProducts(recipeProductsSKUs, 'product_model')
        fetchProducts(recipeToolsSKUs, 'tools')
    }, [recipe])

    // Build utensils list
    const allUtensils = [
        ...(utensilsList?.utensils?.map(
            (u) => `${u?.quantity || ''} ${u?.utensil?.[0]?.title || ''} ${u?.note || ''}`,
        ) || []),
        ...(utensilsList?.product_model?.map(
            (u) => `${u?.title || u?.description || ''} ${u?.note || ''}`,
        ) || []),
        ...(utensilsList?.tools?.map((u) => `${u?.description || ''} ${u?.note || ''}`) || []),
    ]

    const isColumn = useBreakpointValue({base: true, sm: true, md: true, lg: false})

    useEffect(() => {
        const updateHeight = () => {
            if (!recipe) return
            if (!isColumn) {
                if (ingredientsRef.current && stepsRef.current) {
                    const left = ingredientsRef.current.offsetHeight
                    const right = stepsRef.current.offsetHeight
                    setDarkBgHeight(Math.max(left, right))
                }
            } else {
                setDarkBgHeight(ingredientsRef.current.offsetHeight + 75)
            }
        }
        updateHeight()
        window.addEventListener('resize', updateHeight)
        return () => window.removeEventListener('resize', updateHeight)
    }, [recipe, isColumn])

    // Breadcrumbs
    const breadcrumbCategories = [
        {
            id: 'home',
            name: intl.formatMessage({
                id: 'recipes.listing_page.breadcrumbs.return_home',
                defaultMessage: 'Return to homepage',
            }),
            c_slugUrl: '/',
        },
        {
            id: 'recipes_page',
            name: intl.formatMessage({
                id: 'recipes.listing_page.breadcrumbs.recipes',
                defaultMessage: 'Recipes',
            }),
            c_slugUrl: '/r/recipes',
        },
        {id: 'recipe_detail_page', name: recipe?.[0]?.title, c_slugUrl: path},
    ]

    if (!recipe) return <LoadingSpinner />

    return (
        <Flex {...details?.detailsContainer}>
            {/* Breadcrumbs */}
            <Flex {...details?.breadcrumbsContainer} layerStyle="page">
                <Breadcrumb categories={breadcrumbCategories} w="100%" h="100%" />
            </Flex>

            {/* Hero Section */}
            <Flex justifyContent="center" width="100%" bg="background.light-dark">
                {recipe.map((rec, idx) => (
                    <RecipeHeroBanner
                        key={idx}
                        title={rec?.recipe_title}
                        description={rec?.description}
                        mediaData={
                            rec.recipe_media?.recipe_image ?? rec.recipe_media?.recipe_video ?? null
                        }
                        recipeInfo={{
                            author: rec?.author,
                            time: rec?.recipe_time,
                            classification: rec?.recipe_classification,
                        }}
                    />
                ))}
            </Flex>

            {/* Product Suggestions */}
            {productsUsedInRecipe.length > 0 && (
                <Flex width="100%" layerStyle="page">
                    <RecipeProductSuggestions
                        textBoxTitle={intl.formatMessage({
                            id: 'recipes.details_page.carousel.can_also_prepare_with.text',
                            defaultMessage: 'This recipe is prepared with:',
                        })}
                        madeWith={productsUsedInRecipe}
                        isMainProduct
                        toolsGroup={toolsUsedInRecipe}
                    />
                </Flex>
            )}

            {/* Ingredients + Steps Section */}
            <Box position="relative" w="100%">
                {/* Background layer */}
                <Box
                    {...styles?.recipeInfoSudoContainer}
                    _before={{
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        w: {base: '100%', lg: '50%'},
                        h: {base: `${darkBgHeight}px`, lg: '100%'},
                        bg: 'background.light-dark',
                    }}
                />

                {/* Foreground content */}
                <Flex
                    layerStyle="page"
                    {...details?.recipeInfoContainer}
                    position="relative"
                    zIndex={1}
                >
                    {recipe.map((rec, idx) => (
                        <Box
                            key={`ingredients-${idx}`}
                            ref={ingredientsRef}
                            w={{base: '100%', lg: '50%'}}
                            position={{base: 'relative', lg: 'sticky'}}
                            top={{base: '0', lg: 0}}
                            alignSelf="flex-start"
                        >
                            <IngredientsMenu
                                ingredients={rec?.ingredients}
                                servings={rec?.makes_servings}
                                allUtensils={allUtensils}
                            />
                        </Box>
                    ))}

                    {recipe.map((rec, idx) => (
                        <Box key={`steps-${idx}`} ref={stepsRef} w={{base: '100%', lg: '50%'}}>
                            <RecipeStepsMenu recipeSteps={rec?.recipe_steps} />
                        </Box>
                    ))}
                </Flex>
            </Box>

            {/* Additional product suggestions */}
            {productsUsedInRecipe.length > 1 && (
                <Flex width="100%" layerStyle="page">
                    <RecipeProductSuggestions
                        textBoxTitle={intl.formatMessage({
                            id: 'recipes.details_page.carousel.can_also_prepare_with.text',
                            defaultMessage: 'You can also prepare this recipe* with:',
                        })}
                        madeWith={productsUsedInRecipe}
                        isMainProduct={false}
                    />
                </Flex>
            )}
        </Flex>
    )
}

export default RecipeDetails
